import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Slide,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

/* ── Slide-up transition ──────────────────────────────────────────── */
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ── Types ────────────────────────────────────────────────────────── */
export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" = red confirm button (default), "warning" = amber, "info" = brand blue */
  variant?: "danger" | "warning" | "info";
}

type ResolverFn = (value: boolean) => void;

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

/* ── Context ──────────────────────────────────────────────────────── */
const ConfirmContext = createContext<ConfirmContextValue | null>(null);

/* ── Provider ─────────────────────────────────────────────────────── */
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({ message: "" });
  const resolverRef = useRef<ResolverFn | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleClose = (result: boolean) => {
    setOpen(false);
    resolverRef.current?.(result);
    resolverRef.current = null;
  };

  const accentColor =
    options.variant === "warning"
      ? "#f59e0b"
      : options.variant === "info"
      ? "#2f3891"
      : "#dc2626";

  const confirmBg =
    options.variant === "warning"
      ? "linear-gradient(135deg,#f59e0b,#d97706)"
      : options.variant === "info"
      ? "linear-gradient(135deg,#2f3891,#252e7a)"
      : "linear-gradient(135deg,#dc2626,#b91c1c)";

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}

      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted={false}
        onClose={() => handleClose(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          },
        }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 999 }}
      >
        {/* Header strip */}
        <Box
          sx={{
            background: confirmBg,
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <WarningAmberRoundedIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: "#fff",
              lineHeight: 1.2,
            }}
          >
            {options.title ?? "Confirm Action"}
          </Typography>
        </Box>

        {/* Body */}
        <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography
            sx={{ fontSize: "0.9rem", color: "#374151", lineHeight: 1.6 }}
          >
            {options.message}
          </Typography>
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            gap: 1.5,
            borderTop: "1px solid #f1f5f9",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
            onClick={() => handleClose(false)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
              borderColor: "#e2e8f0",
              color: "#64748b",
              "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
            }}
          >
            {options.cancelLabel ?? "Cancel"}
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={<CheckIcon sx={{ fontSize: 15 }} />}
            onClick={() => handleClose(true)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.5,
              background: confirmBg,
              boxShadow: `0 2px 8px ${accentColor}55`,
              "&:hover": {
                background: confirmBg,
                opacity: 0.9,
                boxShadow: `0 4px 16px ${accentColor}66`,
              },
            }}
          >
            {options.confirmLabel ?? "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
};

/* ── Hook ─────────────────────────────────────────────────────────── */
export const useConfirm = (): ((options: ConfirmOptions) => Promise<boolean>) => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx.confirm;
};
